import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { usePartyMutation } from "Api/Mutations/Party/usePartyMutation";
import { merchantKeys } from "Api/Queries/Merchants/useGetMerchantQuery";
import { merchantsListKeys } from "Api/Queries/Merchants/useGetMerchantsQuery";
import { getMerchantPartySchema } from "Components/Hunter/Merchants/CompleteRegistration/Tabs/MerchantParty/schema";
import { MerchantPartyInfo } from "Components/Hunter/Merchants/CompleteRegistration/Tabs/MerchantParty/MerchantPartyInfo";
import { FormSection } from "Components/Shared/FormSection";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { PhoneInput } from "Components/Shared/Inputs/Form/PhoneInput";
import { ContactPersons } from "Components/Shared/Merchant/ContactPersons/ContactPersons";
import type { PartyDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";

type FormModel = z.infer<ReturnType<typeof getMerchantPartySchema>>;

const CONTACT_PERSON_RESOURCES =
  Resources.Hunter.Merchants.CompleteRegistration.ContactPerson;

type Props = {
  party: PartyDto;
  merchantPublicID: string;
  onAfterSubmit: () => void;
  formId: string;
};

export const MerchantPartyForm = ({
  party,
  merchantPublicID,
  onAfterSubmit,
  formId,
}: Props) => {
  const queryClient = useQueryClient();
  const { t } = useResource();

  const {
    handleSubmit,
    control,
    formState: { isDirty },
    reset,
  } = useForm<FormModel>({
    resolver: zodResolver(getMerchantPartySchema(t)),
    defaultValues: {
      phone: party.phone,
      email: party.email ?? undefined,
    },
  });

  const { mutateAsync: putPartyAsync, isPending } = usePartyMutation();

  const onSubmit = async (formData: FormModel) => {
    if (isPending) {
      return;
    }

    if (!isDirty) {
      return onAfterSubmit();
    }

    const { publicID } = party;

    if (!publicID) {
      toast.error(
        t(
          Resources.Hunter.Merchants.CompleteRegistration
            .SaveMerchantPartyError,
        ),
      );
      return;
    }

    try {
      await putPartyAsync({
        publicID,
        data: { ...party, ...formData },
      });
      reset(formData);
      toast.success(
        t(
          Resources.Hunter.Merchants.CompleteRegistration
            .SaveMerchantPartySuccess,
        ),
      );
      queryClient.invalidateQueries({ queryKey: merchantsListKeys.all });
      queryClient.invalidateQueries({
        queryKey: merchantKeys.byMerchantPublicId(merchantPublicID),
      });
    } catch {
      toast.error(
        t(
          Resources.Hunter.Merchants.CompleteRegistration
            .SaveMerchantPartyError,
        ),
      );
      return;
    }

    onAfterSubmit();
  };

  return (
    <>
      <form id={formId} onSubmit={handleSubmit(onSubmit)}>
        <MerchantPartyInfo party={party} />
        <FormSection legend={t(Resources.Common.Contact)} noMargins>
          <PhoneInput
            control={control}
            name="phone"
            label={t(Resources.Common.Phone)}
          />
          <FormInput
            control={control}
            name="email"
            label={t(Resources.Common.Email)}
          />
        </FormSection>
      </form>
      <ContactPersons
        publicPartyID={party.publicID ?? ""}
        title={t(CONTACT_PERSON_RESOURCES.OptionalTitle)}
        noItemsText={t(CONTACT_PERSON_RESOURCES.OptionalNoItems)}
      />
    </>
  );
};
